import { DefaultGameState, GameState, InventoryItem } from "@shared/types";
import { html } from "../helpers/webComponents";
import { GameRouteService } from "../services/GameRouteService";

/**
 * Inventory component
 */
export class InventoryComponent extends HTMLElement {
    private inventoryVisible = false;

    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();
        this._currentGameState = state as DefaultGameState;
        this.render();
    }

    /**
     * Toggle the inventory visibility (normally happens when clicking the backpack icon)
     */
    private toggleInventory(): void {
        this.inventoryVisible = !this.inventoryVisible;
        const inventoryMenu: HTMLElement = this.shadowRoot!.querySelector(".inventory-menu")!;
        inventoryMenu.classList.toggle("visible", this.inventoryVisible);
    }

    /**
     * Render the inventory component
     */
    private render(): void {
        if (!this.shadowRoot) return;
        if (!this._currentGameState) return;

        const items: string[] = [];
        const inventory: InventoryItem[] = this._currentGameState.inventory;
        for (let i: number = 0; i < 15; i++) {
            if (inventory[i]) {
                items.push(`<div class="inventory-item">
                                <img src="${inventory[i].icon}" />
                                <div class="tooltip">${inventory[i].name}</div>
                            </div>`);
            }
            else {
                items.push("<div class=\"inventory-item\"></div>");
            }
        }

        const inventoryMenu: HTMLElement = html`
            <div class="inventory-menu ${this.inventoryVisible ? "visible" : ""}">
                <h2 class="inventory-title">Inventory</h2>
                <div class="inventory-grid">
                    ${items.join("")}
                </div>
            </div>
        `;

        const inventoryIcon: HTMLElement = html`
            <button class="inventory-icon">
                <img src="/assets/img/icons/backpack.png" width="36px" height="36px" alt="Inventory" />
            </button>
        `;
        inventoryIcon.addEventListener("click", () => this.toggleInventory());

        this.addStyles();

        this.shadowRoot.appendChild(inventoryIcon);
        this.shadowRoot.appendChild(inventoryMenu);
    }

    /**
     * Add the styles to the shadow DOM for the inventory
     */
    private addStyles(): void {
        this.shadowRoot!.appendChild(html`
            <style>
                .inventory-icon {
                    position: fixed;
                    right: 10px;
                    bottom: 10px; 
                    width: 50px;
                    height: 50px;
                    border-radius: 50%;
                    background: #1a1a1a;
                    font-size: 24px;
                    cursor: pointer;
                    border: none;
                    transition: background 0.3s ease;
                }

                .inventory-icon:hover {
                    background: #555555;
                }

                .inventory-menu {
                    position: fixed;
                    right: 10px;
                    bottom: 70px;
                    width: 270px;
                    background: black;
                    border: 1px solid #252525;
                    padding-left: 20px;
                    padding-right: 20px;
                    border-radius: 15px;
                    opacity: 0;
                    transform: translateY(10px);
                    transition: opacity 0.3s ease, transform 0.3s ease;
                    pointer-events: none;
                }

                .inventory-menu.visible {
                    opacity: 1;
                    transform: translateY(0);
                    pointer-events: auto;
                }

                .inventory-grid {
                    display: grid;
                    grid-template-columns: repeat(5, 50px);
                    grid-template-rows: repeat(3, 50px);
                    gap: 5px;
                    margin-bottom: 10px;
                }

                .inventory-item {
                    background: #333;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 1px solid #555555;
                    border-radius: 5px;
                }

                .inventory-item img {
                    width: 50px;
                    height: 50px;
                    object-fit: contain;
                }

                .inventory-title {
                    margin-top: 10px;
                    margin-bottom: 10px;
                    color: #f4c842;
                }

                .tooltip {
                    position: absolute;
                    background: #212121;
                    padding: 5px 10px;
                    border-radius: 4px;
                    font-size: 14px;
                    top: -30px;
                    left: 50%;
                    transform: translateX(-50%);
                    opacity: 0;
                    pointer-events: none;
                    transition: opacity 0.2s ease-in-out;
                }

                .inventory-item:hover .tooltip {
                    opacity: 1;
                }
            </style>
        `);
    }
}
